/* eslint-disable @typescript-eslint/ban-ts-comment */
import { type WorkflowStep3Result } from '~/lib/mbue-api/types'
import { type WebViewerInstance } from '@pdftron/webviewer'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { updateWorkflowStepResult } from '~/lib/mbue-api/services/workflowStepResult'
import { useMutation } from '@tanstack/react-query'
import { useUpdateStepTaskResult } from '~/hooks/submittals'
import { useUnsavedChangesWarning } from '~/hooks/useUnsavedChangesWarning'
import { isNil } from 'lodash'

import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from '../ui/Table'
import Button from '~/components/ui/Button'
import {
  SquarePenIcon,
  FilePenIcon,
  Trash2Icon,
  XIcon,
  PlusIcon,
  Loader2Icon
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '../ui/DropdownMenu'
import Textarea from '../ui/Textarea'
import { Label } from '@radix-ui/react-label'
import {
  AlertDialog,
  AlertDialogHeader,
  AlertDialogCancel,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription
} from '../ui/AlertDialog'

declare global {
  interface Window {
    WebViewer?: {
      getInstance: (viewer?: HTMLDivElement) => WebViewerInstance
      length: number
    }
  }
}

const statusMap = {
  confirmed: 'Confirmed',
  confirming: 'Confirm',
  review: 'Review'
}

const statusColorMap = {
  confirmed: 'bg-[#34A853] text-white hover:bg-[#34A853]/90',
  confirming: 'bg-[#E44386] text-white hover:bg-[#E44386]/90',
  review: 'bg-[#707070] text-white hover:bg-[#707070]/90'
}

interface ConfirmProductRequirementsTableProps {
  workflowStep3Result?: WorkflowStep3Result
  viewerRef: React.RefObject<HTMLDivElement>
  selectedRequirement: {
    specificationIndex: number
    requirementIndex: number
  } | null
  setSelectedRequirement: (
    requirement: {
      specificationIndex: number
      requirementIndex: number
    } | null
  ) => void
  setIsDocumentLoading: (isLoading: boolean) => void
  isStepCompleted: boolean
}

export default function ConfirmProductRequirementsTable({
  workflowStep3Result,
  viewerRef,
  selectedRequirement,
  setSelectedRequirement,
  setIsDocumentLoading,
  isStepCompleted
}: ConfirmProductRequirementsTableProps) {
  const navigate = useNavigate()

  const [isEditingRequirement, setIsEditingRequirement] = useState<{
    specificationIndex: number
    requirementIndex: number
  } | null>(null)

  const [isDeletingRequirement, setIsDeletingRequirement] = useState<{
    specificationIndex: number
    requirementIndex: number
  } | null>(null)

  const [productRequirements, setProductRequirements] = useState<
    WorkflowStep3Result | null | undefined
  >(workflowStep3Result)

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const [unconfirmProductRequirement, setUnconfirmProductRequirement] = useState<{
    specificationIndex: number
    requirementIndex: number
  } | null>(null)

  const blocker = useUnsavedChangesWarning(hasUnsavedChanges)

  const specifications =
    (Array.isArray(productRequirements)
      ? (productRequirements[0] as unknown as WorkflowStep3Result).requirements_list.rows
      : productRequirements?.requirements_list.rows) ?? []

  const { orgId, projectId, workflowInstanceId } = useParams<{
    orgId: string
    projectId: string
    workflowInstanceId: string
  }>()

  const { mutateAsync: updateStepTaskResult } = useUpdateStepTaskResult(
    parseInt(projectId!, 10),
    workflowInstanceId!,
    3,
    0,
    {
      onSuccess: () => {
        setIsDocumentLoading(false)
        navigate(
          `/org/${orgId}/project/${projectId}/submittals/workflow/${workflowInstanceId}`
        )
      }
    }
  )

  const { mutate: updateProductRequirements, isPending: isUpdatingProductRequirements } =
    useMutation({
      mutationFn: updateWorkflowStepResult,
      onSuccess: async () => {
        const allApproved = productRequirements?.requirements_list.rows.every((row) =>
          row.requirements.every((requirement) => requirement.approved)
        )

        if (allApproved) {
          await updateStepTaskResult({
            status: true
          })
          toast.success('Product requirements confirmed successfully')
        } else {
          navigate(
            `/org/${orgId}/project/${projectId}/submittals/workflow/${workflowInstanceId}`
          )
        }
      },
      onError: () => {
        toast.error('Error confirming product requirements')
      }
    })

  useEffect(() => {
    if (Array.isArray(workflowStep3Result)) {
      setProductRequirements(workflowStep3Result[0] as unknown as WorkflowStep3Result)
      return
    }
    setProductRequirements(workflowStep3Result)
  }, [workflowStep3Result])

  const onComplete = () => {
    if (isUpdatingProductRequirements || !productRequirements) return
    if (productRequirements.requirements_list.rows.length === 0) return

    setHasUnsavedChanges(false)

    updateProductRequirements({
      projectId: Number(projectId),
      workflowInstanceId: workflowInstanceId!,
      stepIndex: 3,
      stepData: {
        result: productRequirements
      }
    })
  }

  const onActionButtonClick = async (
    specificationIndex: number,
    requirementIndex: number
  ) => {
    if (!productRequirements) return

    const requirementsList = productRequirements.requirements_list.rows

    const currentRequirement =
      requirementsList[specificationIndex].requirements[requirementIndex]

    if (currentRequirement.approved === true) {
      if (currentRequirement.points.length > 0) {
        await highlightPdfText(specificationIndex, requirementIndex)
      }
      setUnconfirmProductRequirement({
        specificationIndex,
        requirementIndex
      })

      return
    }

    if (
      selectedRequirement?.specificationIndex === specificationIndex &&
      selectedRequirement.requirementIndex === requirementIndex &&
      !isStepCompleted
    ) {
      const nextProductRequirements = structuredClone(productRequirements)
      nextProductRequirements.requirements_list.rows[specificationIndex].requirements[
        requirementIndex
      ].approved = true
      setProductRequirements(nextProductRequirements)
      setSelectedRequirement(null)
      if (!hasUnsavedChanges) {
        setHasUnsavedChanges(true)
      }
      return
    }

    if (
      requirementsList[specificationIndex].requirements[requirementIndex].points.length >
      0
    ) {
      await highlightPdfText(specificationIndex, requirementIndex)
    } else {
      setSelectedRequirement({
        specificationIndex,
        requirementIndex
      })
    }
  }

  const highlightPdfText = async (
    specificationIndex: number,
    requirementIndex: number
  ) => {
    if (!productRequirements) return
    if (!window.WebViewer || !viewerRef.current) return

    const specification = productRequirements.requirements_list.rows[specificationIndex]
    const instance = window.WebViewer.getInstance(viewerRef.current)
    const { Core } = instance
    const { documentViewer } = Core
    const currentRequirement = specification.requirements[requirementIndex]
    const scrollElement = documentViewer.getScrollViewElement()

    const pageNumber = currentRequirement.points[0].source_page
    const firstPoint = currentRequirement.points[0]

    documentViewer.setCurrentPage(pageNumber, false)
    documentViewer.setFitMode(documentViewer.FitMode.FitWidth)

    setSelectedRequirement({
      specificationIndex,
      requirementIndex
    })

    await new Promise((resolve) => setTimeout(resolve, 100))
    const currentZoom = documentViewer.getZoomLevel()
    documentViewer.zoomTo(
      currentZoom,
      scrollElement.scrollLeft,
      scrollElement.scrollTop + firstPoint.coordinates.ymin
    )
  }

  const constOnEditSave = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (isEditingRequirement === null || !productRequirements) return

    const formData = new FormData(event.target as HTMLFormElement)
    const summary = formData.get('summary') as string
    const comment = formData.get('comment') as string

    if (isEditingRequirement.requirementIndex >= 0) {
      const nextProductRequirements = structuredClone(productRequirements)
      nextProductRequirements.requirements_list.rows[
        isEditingRequirement.specificationIndex
      ].requirements[isEditingRequirement.requirementIndex].summary = summary
      nextProductRequirements.requirements_list.rows[
        isEditingRequirement.specificationIndex
      ].requirements[isEditingRequirement.requirementIndex].comment = comment
      nextProductRequirements.requirements_list.rows[
        isEditingRequirement.specificationIndex
      ].requirements[isEditingRequirement.requirementIndex].is_edited = true

      setProductRequirements(nextProductRequirements)
      setSelectedRequirement(isEditingRequirement)
      setIsEditingRequirement(null)
      return
    }

    if (isEditingRequirement.requirementIndex === -1) {
      const nextProductRequirements = structuredClone(productRequirements)
      nextProductRequirements.requirements_list.rows[
        isEditingRequirement.specificationIndex
      ].requirements.push({
        summary,
        comment,
        is_edited: true,
        approved: true,
        points: []
      })

      setProductRequirements(nextProductRequirements)
      setIsEditingRequirement(null)
    }
  }

  const onEditDelete = () => {
    if (isDeletingRequirement === null || !productRequirements) return

    const nextProductRequirements = structuredClone(productRequirements)
    nextProductRequirements.requirements_list.rows[
      isDeletingRequirement.specificationIndex
    ].requirements.splice(isDeletingRequirement.requirementIndex, 1)
    setProductRequirements(nextProductRequirements)
    setIsDeletingRequirement(null)
  }

  const onUnconfirmProductClick = (
    specificationIndex: number,
    requirementIndex: number
  ) => {
    if (!productRequirements) return
    const nextProductRequirements = structuredClone(productRequirements)
    nextProductRequirements.requirements_list.rows[specificationIndex].requirements[
      requirementIndex
    ].approved = false
    setProductRequirements(nextProductRequirements)
    setUnconfirmProductRequirement(null)
    if (!hasUnsavedChanges) {
      setHasUnsavedChanges(true)
    }
  }

  const allApproved = specifications.every((specification) =>
    specification.requirements.every((requirement) => requirement.approved)
  )

  return (
    <div className="w-full bg-[#333]">
      <div className="h-[52px] w-full bg-[#333] flex justify-between items-center px-2 text-sm">
        <div className="text-white flex text-xs">
          Confirm the following requirements are met in accordance with the specifications
          section.
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" className="h-8" onClick={() => navigate(-1)}>
            Exit
          </Button>
          <Button
            className="h-8 flex items-center gap-1"
            onClick={onComplete}
            disabled={isStepCompleted}
          >
            {isUpdatingProductRequirements && (
              <Loader2Icon className="w-4 h-4 animate-spin" strokeWidth={3} />
            )}
            {allApproved ? 'Complete' : 'Save & Exit'}
          </Button>
        </div>
      </div>
      <div className="p-2 h-[calc(100vh-108px)] overflow-y-auto">
        <div className="relative w-full bg-foreground">
          <div
            className={
              !isNil(unconfirmProductRequirement) || !isNil(isEditingRequirement)
                ? 'bg-white flex flex-col gap-2 h-[calc(100vh_-_118px)]'
                : 'bg-white flex flex-col gap-2'
            }
          >
            {specifications.map((specification, spec_index) => {
              const requirements = specification.requirements ?? []

              return (
                <Table key={specification.specification_number}>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        colSpan={2}
                        className="border-r bg-gray-200 font-semibold px-2"
                      >
                        <div className="flex items-center justify-between">
                          <p>
                            Requirements: {specification.specification_number} -{' '}
                            {specification.specification_title}
                          </p>
                          <Button
                            onClick={() =>
                              setIsEditingRequirement({
                                specificationIndex: spec_index,
                                requirementIndex: -1
                              })
                            }
                            variant="outline"
                            className="h-8 min-w-[108px]"
                            disabled={isStepCompleted}
                          >
                            <PlusIcon className="w-4 h-4" />
                            Add
                          </Button>
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requirements.map((requirement, index) => {
                      const isSelected =
                        selectedRequirement?.specificationIndex === spec_index &&
                        selectedRequirement.requirementIndex === index

                      const buttonClasses =
                        isSelected && !requirement.approved
                          ? statusColorMap.confirming
                          : statusColorMap[requirement.approved ? 'confirmed' : 'review']

                      const buttonLabel =
                        isSelected && !requirement.approved
                          ? 'Confirm'
                          : statusMap[requirement.approved ? 'confirmed' : 'review']

                      return (
                        <TableRow key={`requirement-${index}`}>
                          <TableCell>{requirement.summary}</TableCell>
                          <TableCell>
                            <div className="flex gap-1 items-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    disabled={isStepCompleted}
                                  >
                                    <SquarePenIcon className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="shadow-xl">
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() =>
                                      setIsEditingRequirement({
                                        specificationIndex: spec_index,
                                        requirementIndex: index
                                      })
                                    }
                                  >
                                    <div className="flex items-center gap-2">
                                      <FilePenIcon className="w-4 h-4" />
                                      Edit Row
                                    </div>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() =>
                                      setIsDeletingRequirement({
                                        specificationIndex: spec_index,
                                        requirementIndex: index
                                      })
                                    }
                                  >
                                    <div className="flex items-center gap-2">
                                      <Trash2Icon className="w-4 h-4" />
                                      Delete Requirement
                                    </div>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>

                              <Button
                                variant="secondary"
                                className={`h-8 ${buttonClasses} min-w-[108px]`}
                                onClick={() =>
                                  void onActionButtonClick(spec_index, index)
                                }
                              >
                                {buttonLabel}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )
            })}
          </div>
          {isEditingRequirement !== null && (
            <>
              {/* Dialog */}
              <div
                role="dialog"
                className="absolute w-full h-[calc(100vh_-_118px)] top-0 left-0"
              >
                <div className="absolute inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
                {/* Dialog Content */}
                <div className="absolute left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
                  {/* Dialog Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <h2 className="text-lg font-semibold">
                        {isEditingRequirement.requirementIndex === -1
                          ? 'Add Requirement'
                          : 'Edit Requirement'}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {isEditingRequirement.requirementIndex === -1
                          ? 'Add a new requirement to the list.'
                          : 'Make changes to the requirement or add a comment here. Click save when you&apos;re done.'}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="fixed top-0 right-0"
                      onClick={() => setIsEditingRequirement(null)}
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Content */}
                  <form onSubmit={constOnEditSave}>
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-1">
                        <Label className="text-sm font-medium">Product Requirement</Label>
                        <Textarea
                          name="summary"
                          defaultValue={
                            isEditingRequirement.requirementIndex === -1
                              ? ''
                              : productRequirements?.requirements_list.rows[
                                  isEditingRequirement.specificationIndex
                                ].requirements[isEditingRequirement.requirementIndex]
                                  .summary
                          }
                          // @ts-expect-error
                          style={{ fieldSizing: 'content' }}
                        ></Textarea>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label className="text-sm font-medium">Comment</Label>
                        <Textarea
                          name="comment"
                          // @ts-expect-error
                          style={{ fieldSizing: 'content' }}
                          defaultValue={
                            isEditingRequirement.requirementIndex === -1
                              ? ''
                              : (productRequirements?.requirements_list.rows[
                                  isEditingRequirement.specificationIndex
                                ].requirements[isEditingRequirement.requirementIndex]
                                  .comment ?? '')
                          }
                        ></Textarea>
                      </div>
                    </div>
                    {/* Footer */}
                    <div className="flex justify-end gap-2 mt-4">
                      <Button
                        variant="outline"
                        className="min-w-[108px]"
                        onClick={() => setIsEditingRequirement(null)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" className="min-w-[108px]">
                        Save
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </>
          )}

          {!isNil(unconfirmProductRequirement) && (
            <>
              {/* Dialog */}
              <div
                role="dialog"
                className="absolute w-full h-[calc(100vh_-_118px)] top-0 left-0"
              >
                <div className="absolute inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
                {/* Dialog Content */}
                <div className="absolute left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
                  {/* Dialog Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <h2 className="text-lg font-semibold">Unconfirm Product</h2>
                      <p className="text-sm text-gray-500">
                        Are you sure you want to unconfirm the product <br />
                        {
                          productRequirements?.requirements_list.rows[
                            unconfirmProductRequirement.specificationIndex
                          ].requirements[unconfirmProductRequirement.requirementIndex]
                            .summary
                        }
                        ?
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="fixed top-0 right-0"
                      onClick={() => setUnconfirmProductRequirement(null)}
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  {/* Content */}
                  <div>
                    {/* Footer */}
                    <div className="flex justify-end gap-2 mt-4">
                      <Button
                        variant="outline"
                        className="min-w-[108px]"
                        onClick={() => setUnconfirmProductRequirement(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="min-w-[108px]"
                        onClick={() =>
                          onUnconfirmProductClick(
                            unconfirmProductRequirement.specificationIndex,
                            unconfirmProductRequirement.requirementIndex
                          )
                        }
                      >
                        Unconfirm
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          <AlertDialog open={isDeletingRequirement !== null}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Requirement</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this requirement? This action cannot be
                  undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setIsDeletingRequirement(null)}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction onClick={onEditDelete}>Continue</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={blocker.state === 'blocked'}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                <AlertDialogDescription>
                  You have unsaved changes. Are you sure you want to leave?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => blocker.state === 'blocked' && blocker.reset()}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => blocker.state === 'blocked' && blocker.proceed()}
                >
                  Continue
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  )
}
